import { useState, useMemo, useEffect } from "react";
import MaterialReactTableWithSearch from "../../../components/MaterialReactTable/MaterialReactTableWithSearch";
import moment from "moment";
import { APP_ROUTES } from "../../../config/appRoutes";
import { Box, IconButton, Tooltip, Card, CardContent, Grid, Divider } from "@mui/material";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import { useNavigate } from 'react-router-dom';
import { useData } from "../../../context/DataContext";
import LoadingComponent from "../../../components/Loading/LoadingComponent";
import { deleteTrainingPlan } from '../../../context/createAPI'
import * as XLSX from 'xlsx';
import SingleSelect from "../../../components/Selections/singleSelect";
import UpdateIcon from '@mui/icons-material/Update';
import SnackbarMessage from "../../../components/snackBar/snackBar";

function TrainingHistory() {
  const navigate = useNavigate();
  const { user = {}, loading, trainingPlans = [], years = [], levels = [], handleTrainingPlans, handleTrainingReport, trainingTypes = [], } = useData();

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const sortedTrainingPlans = useMemo(() => {
    return trainingPlans
      .map((item) => ({
        ...item,
        month: new Date(item.month_of_training * 1000).toLocaleString(
          "default",
          { month: "short" }
        ),
      }))
      .sort(
        (a, b) =>
          new Date(a.month_of_training * 1000) -
          new Date(b.month_of_training * 1000)
      );
  }, [trainingPlans]);

  const [yearId, setYearId] = useState(null);
  const [trainingTypeId, setTrainingTypeId] = useState(null);
  const [levelId, setLevelId] = useState(null);
  const [filteredTrainings, setFilteredTrainings] = useState([])


  useEffect(() => {
    if (years?.length > 0) {
      setYearId(years[0].value);
    }
    if (levels?.length > 0) {
      setLevelId(levels[0].value);
    }
    if (trainingTypes?.length > 0) {
      setTrainingTypeId(trainingTypes[0]?.value);
    }
  }, [years, levels,]);


  useEffect(() => {
    if (!yearId && !levelId && !trainingTypeId) {
      setFilteredTrainings(sortedTrainingPlans);
      return;
    }

    const filteredData = sortedTrainingPlans.filter(
      (item) =>
        (!yearId || +item.year === +yearId) &&
        (!levelId || +item.level_id === +levelId) &&
        (!trainingTypeId || +item.training_type_id === +trainingTypeId)
    );

    setFilteredTrainings(filteredData);
  }, [yearId, levelId, trainingTypeId, sortedTrainingPlans]);



  const handleInputChange = (name, value) => {
    if (name === "years") {
      setYearId(value);
    } else if (name === "levelId") {
      setLevelId(value);
    } else if (name === "trainingType") {
      setTrainingTypeId(value);
    }
  };


  const trainingsHistoryColumn = useMemo(
    () => [
      {
        accessorFn: (row, index) => index + 1,
        header: 'SNO',
        id: 'id',
        size: 80,
        minSize: 50,
        enableGlobalFilter: false,
      },
      {
        accessorKey: "trainingType_name",
        header: "Training Type",
        Cell: ({ row }) => {
          const text =
            row.original.trainingType_name != null
              ? row.original.trainingType_name
              : "-";
          return (
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "150px",
                cursor: "pointer",
              }}
            >
              <Tooltip title={text} arrow>
                <span
                  style={{
                    display: "inline-block",
                    maxWidth: "150px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {text}
                </span>
              </Tooltip>
            </div>
          );
        },
      },
      {
        accessorKey: "training_content",
        header: "Training Name",
        Cell: ({ row }) => {
          const text =
            row.original.training_content != null
              ? row.original.training_content
              : "-";
          return (
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "150px",
                cursor: "pointer",
              }}
            >
              <Tooltip title={text} arrow>
                <span
                  style={{
                    display: "inline-block",
                    maxWidth: "150px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {text}
                </span>
              </Tooltip>
            </div>
          );
        },
      },
      {
        accessorKey: "level_id",
        header: "Level",
        size: 80,
        minSize: 50,
        accessorFn: (row) => {
          const level = levels.find(level => level.value === row.level_id);
          return level ? level.label : "-";
        },
      },

      {
        accessorKey: "month_of_training",
        header: "Target Month",
        Cell: ({ row }) => {
          const timestamp = row.original.month_of_training;
          const date = moment.unix(timestamp);
          const monthYear = date.format("MMM/YYYY");
          return <div>{monthYear != null ? monthYear : "-"}</div>;
        },
      },
      {
        accessorKey: "training_topics",
        header: "Training Topic",
        Cell: ({ row }) => {
          const text =
            row.original.training_topics != null
              ? row.original.training_topics
              : "-";
          return (
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "150px",
                cursor: "pointer",
              }}
            >
              <Tooltip title={text} arrow>
                <span
                  style={{
                    display: "inline-block",
                    maxWidth: "150px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {text}
                </span>
              </Tooltip>
            </div>
          );
        },
      },
      {
        accessorKey: "target_group",
        header: "Target Group",
        accessorFn: (row) => row.target_group ?? "-",
        Cell: ({ row }) => {
          const text =
            row.original.target_group !== null
              ? row.original.target_group
              : "-";
          return (
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "150px",
                cursor: "pointer",
              }}
            >
              <Tooltip title={text} arrow>
                <span
                  style={{
                    display: "inline-block",
                    maxWidth: "150px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {text}
                </span>
              </Tooltip>
            </div>
          );
        },
      },
      {
        accessorKey: "resource_person",
        header: "Resource Person",
        Cell: ({ row }) => {
          const text =
            row.original.resource_person != null
              ? row.original.resource_person
              : "-";
          return (
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "150px",
                cursor: "pointer",
              }}
            >
              <Tooltip title={text} arrow>
                <span
                  style={{
                    display: "inline-block",
                    maxWidth: "150px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {text}
                </span>
              </Tooltip>
            </div>
          );
        },
      },
      {
        accessorKey: "training_methodology",
        header: "Training Methodology",
        Cell: ({ row }) => {
          const text =
            row.original.training_methodology != null
              ? row.original.training_methodology
              : "-";
          return (
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "150px",
                cursor: "pointer",
              }}
            >
              <Tooltip title={text} arrow>
                <span
                  style={{
                    display: "inline-block",
                    maxWidth: "150px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {text}
                </span>
              </Tooltip>
            </div>
          );
        },
      },
      {
        accessorKey: "comments",
        header: "Comments",
        Cell: ({ row }) => {
          const text = row.original.comments ? row.original.comments : "-";
          return (
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "150px",
                cursor: "pointer",
              }}
            >
              <Tooltip title={text} arrow>
                <span
                  style={{
                    display: "inline-block",
                    maxWidth: "150px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {text}
                </span>
              </Tooltip>
            </div>
          );
        },
      },
    ], [levels]
  );

  const handleEdit = (row) => {
    const rowData = row.original
    navigate(`${APP_ROUTES.TRAINING.HISTORY_ROUTES.EDIT}`, { state: { rowData } });
  }

  const handleSummaryReport = (row) => {
    const rowData = row.original
    handleTrainingReport(rowData.id)
    navigate(`${APP_ROUTES.TRAINING.HISTORY_ROUTES.REPORT}`, { state: { rowData } });
  }

  const handleDelete = async (row) => {
    const confirmed = window.confirm("Are You Sure You Want to Delete?");
    if (!confirmed) {
      return;
    }

    try {
      const deleteApi = await deleteTrainingPlan({
        id: row.original.id
      });
      if (deleteApi.statusCode === 200) {
        handleTrainingPlans();

        if (years?.length > 0) {
          setYearId(years[0].value);
        }
        if (levels?.length > 0) {
          setLevelId(levels[0].value);
        }
        if (trainingTypes?.length > 0) {
          setTrainingTypeId(trainingTypes[0]?.value);
        }

        setSnackbar({
          open: true,
          message: deleteApi.data ?? "Training plan deleted successfully",
          severity: "success",
        });

      }
    } catch (error) {
      console.error(error);
    }
  };

  const renderRowActions = ({ row }) => (
    <Box sx={{ display: "flex", gap: "1rem" }}>
      <Tooltip title="Update">
        <IconButton>
          <UpdateIcon sx={{ color: "var(--success)" }} onClick={() => handleEdit(row)} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <Tooltip title="Cannot delete once training is started">
          <span>
            <IconButton
              color="error"
              disabled={row.original.is_started === 1}
              onClick={() => handleDelete(row)}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </span>
        </Tooltip>

      </Tooltip>
      <Tooltip title="Summary Report">
        <IconButton >
          <SummarizeOutlinedIcon sx={{ color: "var(--secondary)" }} onClick={() => handleSummaryReport(row)} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  if (loading) {
    return <LoadingComponent />;
  }

  const handleExportToExcel = (data, columns, name) => {
    const formattedData = data.map((row, index) => {
      const formattedRow = { ...row };

      const level = levels.find(level => level.value === row.level_id);
      if (level) {
        formattedRow.level_id = level.label;
      } else {
        formattedRow.level_id = "-";
      }

      if (formattedRow.month_of_training) {
        const timestamp = formattedRow.month_of_training;
        const date = moment.unix(timestamp);
        formattedRow.month_of_training = date.format('MMM/YYYY');
      } else {
        formattedRow.month_of_training = "-";
      }
      return formattedRow;
    });

    const headers = columns.map(col => col.header);
    const sheetData = formattedData.map(row => {
      return columns.reduce((acc, col) => {
        acc[col.header] = row[col.accessorKey];
        return acc;
      }, {});
    });

    const ws = XLSX.utils.json_to_sheet(sheetData, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, name || 'Table Data');
    XLSX.writeFile(wb, `${name || 'table_data'}.xlsx`);
  };


  return (
    <Box>
      <Card elevation={6}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={1}>
            {
              [
                { id: 1, name: "years", label: "Select year", value: yearId, options: years },
                { id: 2, name: "trainingType", label: "Select Type", value: trainingTypeId, options: trainingTypes },
                ...user?.user_level_id === 4 ? [] : [{ id: 3, name: "levelId", label: "Select Level", value: levelId, options: levels }]
              ].map((field) => (
                <Grid item size={{ xs: 12, md: 3 }}>
                  <SingleSelect
                    name={field.name}
                    label={field.label}
                    options={field.options}
                    value={field.value}
                    onChange={handleInputChange}
                  />
                </Grid>
              ))
            }
          </Grid>
        </Box>

        <Divider sx={{ bgcolor: '#f5f1f1ff' }} />

        <CardContent>
          <MaterialReactTableWithSearch
            data={filteredTrainings}
            columns={trainingsHistoryColumn}
            enableRowActions={user.user_level_id !== 4}
            renderRowActions={(props) => {
              if (user.user_level_id === 4) {
                return null;
              }
              return renderRowActions({ ...props });
            }}
            onExportToExcel={handleExportToExcel}
          />
        </CardContent>
      </Card>

      <SnackbarMessage
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  )
}

export default TrainingHistory;
